import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {
	FooterButtonsComponent,
	FormInputNumberComponent,
	FormInputTextComponent,
	FormSelectComponent,
	UtilsService
} from '@shared-lib';
import {finalize, Subscription, take} from 'rxjs';
import {SelectItem} from 'primeng/api';
import {WalletService} from '../../../core/service/kleros/wallet/wallet.service';
import {FormUtentiAutocompleteComponent} from '../../../shared/components/form-utenti-autocomplete/form-utenti-autocomplete.component';
import {AccountService, AziendeService} from '../../../core/service';
import {DialogsUtilsService} from '../../../core/service/utils/dialogs-utils.service';
import {
	AziendaModel,
	getOwnerTypeOptions,
	getTopupMethodOptions,
	OwnerType,
	RechargeWalletRequest,
	TopupMethod,
	Wallet
} from '../../../shared/models';
import {CurrencyPipe, DecimalPipe} from '@angular/common';
import {Button} from 'primeng/button';

@Component({
	selector: 'crea-wallet-dialog',
	imports: [
		FormUtentiAutocompleteComponent,
		TranslatePipe,
		FormInputNumberComponent,
		FormInputTextComponent,
		FormSelectComponent,
		FooterButtonsComponent,
		ReactiveFormsModule,
		DecimalPipe,
		Button
	],
	template: `
    <form [formGroup]="formGroup">
      <div class="fadein flex flex-column gap-3">
        @if (!walletPreselezionato) {
          <form-select [label]="'tipo'|translate"
                       [options]="ownerTypes"
                       [control]="formGroup.controls['tipo']"
                       [placeholder]="'select_ph'|translate"
                       (onChange)="onTipoChange()">
          </form-select>
          @let tipoWallet = formGroup.controls['tipo'].value;
          @if (OwnerType.USER === tipoWallet) {
            <form-utenti-autocomplete class="fadein"
                                      [label]="'email'|translate"
                                      [infoTooltipText]="'utenteWalletWarning'|translate"
                                      [control]="formGroup.controls['mail']">
            </form-utenti-autocomplete>
          }
          @if (OwnerType.COMPANY === tipoWallet) {
            <!-- per l'aziendale si sceglie l'azienda: la mail del borsellino viene ricavata dal backend -->
            <form-select class="fadein"
                         [label]="'azienda'|translate"
                         [options]="aziende"
                         [control]="aziendaFC"
                         [loading]="loadingAziende"
                         optionValue="id" optionLabel="nome"
                         [placeholder]="'select_ph'|translate"
                         (onChange)="onAziendaChange($event)">
            </form-select>
          }
        }

        @let borsellino = walletPreselezionato ?? walletAziendale;
        @if (borsellino) {
          <div class="fadein flex justify-content-between align-items-center surface-100 border-round p-3">
            <div class="flex flex-column gap-1">
              <span class="text-sm text-color-secondary">{{'portafoglio'|translate}}</span>
              <span class="font-bold">{{'ownerType.' + borsellino.ownerType|translate}} - {{borsellino.mail}}</span>
            </div>
            <div class="flex flex-column gap-1 text-right">
              <span class="text-sm text-color-secondary">{{'saldoCorrente'|translate}}</span>
              <span class="font-bold">{{borsellino.currentBalance | number:'1.2-2':LOCALE_IMPORTI}} €</span>
            </div>
          </div>
        }

        <div class="flex flex-column gap-2">
          <form-input-number [label]="'importo'|translate" mode="currency" currency="EUR"
                             [control]="formGroup.controls['amount']">
          </form-input-number>
          <div class="flex align-items-center gap-2 flex-wrap">
            <span class="text-sm text-color-secondary">{{'preset'|translate}}:</span>
            @for (importo of presetImporti; track importo) {
              <p-button type="button" size="small" severity="primary" [outlined]="true"
                        [label]="importo + ' €'"
                        (onClick)="impostaImporto(importo)">
              </p-button>
            }
          </div>
        </div>

        <div class="grid">
          <div class="col-12 sm:col-6">
            <form-select [label]="'metodoAccredito'|translate"
                         [options]="topupMethods"
                         [control]="formGroup.controls['metodoAccredito']"
                         [placeholder]="'select_ph'|translate">
            </form-select>
          </div>
          <div class="col-12 sm:col-6">
            <form-input-text [label]="'riferimentoNote'|translate"
                             [control]="formGroup.controls['riferimento']">
            </form-input-text>
          </div>
        </div>

        @if (borsellino && formGroup.controls['amount'].value) {
          <div class="flex justify-content-between align-items-center px-2 py-3 border-1 border-primary border-round bg-primary-50">
            <span>{{'nuovoSaldoPostRicarica'|translate}}:</span>
            <span class="font-bold">
              {{borsellino.currentBalance + formGroup.controls['amount'].value | number:'1.2-2':LOCALE_IMPORTI}} €
            </span>
          </div>
        }
      </div>
      <div class="sticky-modal-footer pt-3">
        <footer-buttons [confirmText]="'salva'|translate"
                        [dismissText]="'annulla'|translate"
                        [confirmDisabled]="formGroup.invalid"
                        (confirmClicked)="ricaricaWallet()"
                        (dismissClicked)="dialogRef.close()">
        </footer-buttons>
      </div>
    </form>
	`,
	providers: [CurrencyPipe]
})
export class RicaricaWalletDialogComponent implements OnInit, OnDestroy {

	protected dialogRef = inject(DynamicDialogRef);
	protected translate = inject(TranslateService);
	private dialogConfig = inject(DynamicDialogConfig);
	private walletService = inject(WalletService);
	private aziendeService = inject(AziendeService);
	private dialogUtils = inject(DialogsUtilsService);
	private utils = inject(UtilsService);
	private account = inject(AccountService);
	private currencyPipe = inject(CurrencyPipe);

	protected readonly OwnerType = OwnerType;
	// stesso locale usato dal campo importo (form-input-number): così simbolo e separatori coincidono in tutto il popup
	protected readonly LOCALE_IMPORTI = 'it-IT';

	// borsellino già scelto (ricarica da una riga del pannello di gestione borsellini): in questo caso
	// non si sceglie né il tipo né l'intestatario
	protected walletPreselezionato: Wallet = this.dialogConfig.data?.wallet;

	protected ownerTypes: SelectItem<OwnerType>[] = [];
	protected topupMethods: SelectItem<TopupMethod>[] = [];

	protected readonly presetImporti: number[] = [20, 50, 100, 250];

	protected aziende: AziendaModel[] = [];
	protected loadingAziende = false;
	protected aziendaFC: FormControl = new FormControl();
	// borsellino aziendale risolto a partire dall'azienda selezionata
	protected walletAziendale: Wallet;

	formGroup: FormGroup = new FormGroup({
		mail: new FormControl(this.walletPreselezionato?.mail ?? null, Validators.required),
		amount: new FormControl(null, Validators.required),
		tipo: new FormControl(this.walletPreselezionato?.ownerType ?? null),
		metodoAccredito: new FormControl(null),
		riferimento: new FormControl(null),
		executedByUserId: new FormControl(this.account.userData.uid, Validators.required)
	});

	private subscriptions: Subscription = new Subscription();

	ngOnInit() {
		this.ownerTypes = getOwnerTypeOptions(this.translate);
		this.topupMethods = getTopupMethodOptions(this.translate);
		if (!this.walletPreselezionato) {
			this.loadingAziende = true;
			this.subscriptions.add(this.aziendeService.aziende$
				.pipe(take(1), finalize(() => this.loadingAziende = false))
				.subscribe(aziende => this.aziende = aziende));
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	protected impostaImporto(importo: number) {
		this.formGroup.controls['amount'].setValue(importo);
	}

	// cambiando tipo di borsellino la scelta dell'intestatario riparte da zero
	protected onTipoChange() {
		this.formGroup.controls['mail'].reset();
		this.aziendaFC.reset();
		this.walletAziendale = null;
	}

	protected onAziendaChange(idAzienda: number) {
		this.formGroup.controls['mail'].reset();
		this.walletAziendale = null;
		if (!idAzienda) {
			return;
		}
		this.subscriptions.add(this.utils.spinnerRestCall(this.walletService.getWalletAziendale(idAzienda))
			.subscribe((wallet: Wallet) => {
				if (!wallet) {
					// l'azienda selezionata non ha (più) un borsellino aziendale attivo
					this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('noBorsellinoAziendale'));
					this.aziendaFC.reset();
					return;
				}
				this.walletAziendale = wallet;
				this.formGroup.controls['mail'].setValue(wallet.mail);
			}));
	}

	ricaricaWallet() {
		const formValue = this.formGroup.value;
		// il riferimento è un campo libero opzionale: se svuotato deve arrivare null, non stringa vuota
		const request: RechargeWalletRequest = {...formValue, riferimento: formValue.riferimento?.trim() || null};
		this.subscriptions.add(this.utils.spinnerRestCall(this.walletService.rechargeWallet(request), true)
			.subscribe(() => {
				const importoFormattato = this.currencyPipe.transform(request.amount, 'EUR', 'symbol', '1.2-2', 'it-IT');
				this.utils.fireConfirmationToast(this.translate.instant('ricaricaEseguita', {amount: importoFormattato}));
				this.walletService.updateWalletData.next();
				this.dialogRef.close(true);
			}));
	}
}
